import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { ArticleYearlyBreakdownView } from '../../_generated/model/articleYearlyBreakdownView';
import { ArticleSummaryView, ArticleView } from '../../_generated/model/models';
import { ArticlesActions } from './articles.actions';

export const articlesFeatureKey = 'articles';

export interface ArticlesState {
  articles: ArticleSummaryView[];
  articlesYearlyBreakdown: ArticleYearlyBreakdownView[];
  selectedYear: number;
  selectedArticle: ArticleView | null;
  isLoading: boolean;
  loaded: boolean;
  error: string | null;
  latestArticle: ArticleView | null;
  latestArticleLoading: boolean;
  latestArticleLoaded: boolean;
  latestArticleError: string | null;
  hasMorePages: boolean;
}

export const initialArticlesState: ArticlesState = {
  articles: [],
  articlesYearlyBreakdown: [],
  selectedYear: 2025,
  selectedArticle: null,
  isLoading: false,
  loaded: false,
  error: null,
  latestArticle: null,
  latestArticleLoading: false,
  latestArticleLoaded: false,
  latestArticleError: null,
  hasMorePages: true,
};

function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : parseInt(value);
}

export const articlesFeature = createFeature({
  name: articlesFeatureKey,
  reducer: createReducer(
    initialArticlesState,
    on(ArticlesActions.loadArticles, (state, { year }) => ({
      ...state,
      isLoading: true,
      error: null,
      selectedYear: year ?? toNumber(state.selectedYear),
    })),
    on(ArticlesActions.loadArticlesSuccess, (state, { articles }) => ({
      ...state,
      articles,
      isLoading: false,
      loaded: true,
      error: null,
    })),
    on(ArticlesActions.loadArticlesFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error,
    })),
    on(ArticlesActions.selectYear, (state, { year }) => ({
      ...state,
      selectedYear: toNumber(year),
    })),
    on(ArticlesActions.clearArticles, (state) => ({
      ...state,
      articles: [],
      loaded: false,
    })),
    on(ArticlesActions.loadArticlesYearlyBreakdownSuccess, (state, { articlesYearlyBreakdown }) => ({
      ...state,
      articlesYearlyBreakdown: articlesYearlyBreakdown,
      isLoading: false,
      loaded: true,
      error: null,
    })),
    on(ArticlesActions.loadArticleByNodeId, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    })),
    on(ArticlesActions.loadArticleByNodeIdSuccess, (state, { article }) => ({
      ...state,
      selectedArticle: article,
      isLoading: false,
      error: null,
    })),
    on(ArticlesActions.loadArticleByNodeIdFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error,
    })),
    on(ArticlesActions.clearSelectedArticle, (state) => ({
      ...state,
      selectedArticle: null,
    })),
    on(ArticlesActions.loadLatestArticle, (state) => ({
      ...state,
      latestArticleLoading: true,
      latestArticleLoaded: false,
      latestArticleError: null,
    })),
    on(ArticlesActions.loadLatestArticleSuccess, (state, { article }) => ({
      ...state,
      latestArticle: article,
      latestArticleLoading: false,
      latestArticleLoaded: true,
      latestArticleError: null,
    })),
    on(ArticlesActions.loadLatestArticleFailure, (state, { error }) => ({
      ...state,
      latestArticleLoading: false,
      latestArticleLoaded: false,
      latestArticleError: error,
    })),
    on(ArticlesActions.loadMoreArticles, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    })),
    on(ArticlesActions.loadMoreArticlesSuccess, (state, { articles, hasMorePages }) => ({
      ...state,
      articles: [...state.articles, ...articles], // Append new articles to existing ones
      isLoading: false,
      error: null,
      hasMorePages,
    })),
    on(ArticlesActions.loadMoreArticlesFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error,
    })),
  ),
  extraSelectors: ({ selectArticlesState }) => ({
    selectHasMorePages: createSelector(selectArticlesState, (state) => state.hasMorePages),
  }),
});
